import React, { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import UserInitials from "@/components/ui/UserInitials";
import BrandLogo from "./BrandLogo";
import NotificationBell from "./NotificationBell";
import SiteFooter from "./SiteFooter";
import ChangePasswordDialog from "./ChangePasswordDialog";
import MyInterestsDialog from "./MyInterestsDialog";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import {
  MoonStars, Sun, SignOut, User, Bookmark, ChatCircleText,
  ChartLineUp, House, Buildings, UsersThree, Files, ClipboardText, ShieldCheck, Key, BellSimpleRinging,
} from "@phosphor-icons/react";

const NavLink = ({ to, icon: Icon, label, active }) => (
  <Link
    to={to}
    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
      active
        ? "bg-secondary text-foreground"
        : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
    }`}
  >
    <Icon size={16} weight={active ? "bold" : "regular"} />
    <span className="font-medium">{label}</span>
  </Link>
);

const RolePill = ({ role }) => {
  const label = role === "super_admin" ? "Super Admin"
    : role === "investment_team" ? "Investment Team"
    : role === "client" ? "Client"
    : "Investor";
  const cls = role === "super_admin"
    ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
    : role === "investment_team"
    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
    : role === "client"
    ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
    : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold tracking-wide uppercase ${cls}`}>
      {label}
    </span>
  );
};

export default function AppShell() {
  const { user, logout, isTeam, isClient } = useAuth();
  const { theme, toggle } = useTheme();
  const loc = useLocation();
  const navigate = useNavigate();
  const path = loc.pathname;
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showMyInterests, setShowMyInterests] = useState(false);
  const isInvestor = user?.role === "investor";

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
                <BrandLogo className="h-14 w-auto object-contain" />
                <div className="hidden sm:block leading-tight pl-2.5 border-l border-border">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Investor Portal</div>
                </div>
              </Link>
              <nav className="hidden md:flex items-center gap-1">
                <NavLink to="/" icon={House} label={isClient ? "My Raise" : "Dashboard"} active={path === "/"} />
                {!isClient && (
                  <>
                    <NavLink to="/watchlist" icon={Bookmark} label="Watchlist" active={path === "/watchlist"} />
                    <NavLink to="/requests" icon={ChatCircleText} label="Requests" active={path === "/requests"} />
                  </>
                )}
                {isTeam && (
                  <NavLink to="/admin" icon={ChartLineUp} label="Admin" active={path.startsWith("/admin")} />
                )}
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggle}
                data-testid="theme-toggle-btn"
                aria-label="Toggle theme"
                title={theme === "light" ? "Switch to dark" : "Switch to light"}
              >
                {theme === "light" ? <MoonStars size={18} /> : <Sun size={18} />}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-md hover:bg-secondary transition-colors" data-testid="user-menu-trigger">
                    <div className="text-right leading-tight hidden sm:block">
                      <div className="text-[13px] font-medium">{user?.name}</div>
                      <div className="flex justify-end"><RolePill role={user?.role} /></div>
                    </div>
                    <UserInitials name={user?.name} email={user?.email} testId="user-menu-initials" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-xs text-muted-foreground">{user?.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {!isClient && (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/watchlist")}>
                        <Bookmark size={16} className="mr-2" /> Watchlist
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/requests")}>
                        <ChatCircleText size={16} className="mr-2" /> My requests
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {isInvestor && (
                    <DropdownMenuItem onClick={() => setShowMyInterests(true)} data-testid="my-interests-menu-item">
                      <BellSimpleRinging size={16} className="mr-2" /> My interests
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => setShowChangePassword(true)} data-testid="change-password-menu-item">
                    <Key size={16} className="mr-2" /> Change password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                    <SignOut size={16} className="mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>
      {path.startsWith("/admin") && isTeam && (
        <div className="border-b border-border bg-secondary/40">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-1 h-11 overflow-x-auto">
              {[
                ["/admin", "Overview"],
                ["/admin/compliance", "Compliance"],
                ["/admin/disclaimers", "Legal Content"],
                ["/admin/companies", "Companies"],
                ["/admin/users", "Users"],
                ["/admin/investor-firms", "Investor Firms"],
                ["/admin/pending-approvals", "Pending Approvals"],
                ["/admin/pending-edits", "Client Edits"],
                ["/admin/meetings", "Meetings"],
                ["/admin/enquiries", "Enquiries"],
                ["/admin/emails", "Email Log"],
                ["/admin/filters", "Filters"],
                ...(user?.role === "super_admin" ? [["/admin/audit", "Audit Log"]] : []),
              ].map(([to, label]) => {
                const active = path === to || (to !== "/admin" && path.startsWith(to));
                return (
                  <Link
                    key={to}
                    to={to}
                    data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-3 h-full inline-flex items-center text-sm border-b-2 -mb-px transition-colors ${
                      active
                        ? "border-emerald-600 text-foreground font-medium"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {label}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <main className="max-w-[1600px] mx-auto flex-1 w-full">
        <Outlet />
      </main>
      <SiteFooter />
      <ChangePasswordDialog open={showChangePassword} onOpenChange={setShowChangePassword} />
      <MyInterestsDialog open={showMyInterests} onOpenChange={setShowMyInterests} />
    </div>
  );
}
